import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["owner", "admin", "editor"];
const ROLE_LABEL = {
  owner: "Owner",
  admin: "Admin",
  editor: "Editor",
};
const ROLE_DESC = {
  owner: "Full access including managing other accounts and viewing audit log",
  admin: "Full content access and audit log; cannot manage users",
  editor: "Can create and edit content; cannot delete or manage users",
};

const empty = { email: "", name: "", password: "", role: "admin" };

const AdminUsers = () => {
  const { email: myEmail } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name || "", password: "", role: u.role });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password) {
          toast.error("Email and password are required");
          return;
        }
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete account for ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Admin Accounts</h1>
          <p className="text-muted-foreground mt-1">Manage who can edit the township website.</p>
        </div>
        <Button onClick={startCreate} className="bg-primary hover:bg-primary/90" data-testid="admin-users-add-btn">
          <Plus size={16} className="mr-1.5" /> Add user
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {ROLES.map((r) => (
          <Card key={r} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="text-accent" size={16} />
                <div className="font-heading font-semibold">{ROLE_LABEL[r]}</div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{ROLE_DESC[r]}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 w-32" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-5 py-3">
                    {u.email}
                    {u.email === myEmail && <span className="ml-2 text-[10px] uppercase tracking-wider text-accent">you</span>}
                  </td>
                  <td className="px-5 py-3">{u.name || "—"}</td>
                  <td className="px-5 py-3">
                    <span className="inline-block px-2 py-0.5 text-[11px] uppercase tracking-wider rounded-full bg-primary/10 text-primary">
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(u)} data-testid={`admin-user-edit-${u.id}`} aria-label="Edit">
                      <Pencil size={15} />
                    </Button>
                    {u.email !== myEmail && (
                      <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`admin-user-delete-${u.id}`} aria-label="Delete">
                        <Trash2 size={15} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "New admin user"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="u-email">Email</Label>
              <Input
                id="u-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editing}
                className="mt-1.5"
                data-testid="admin-users-field-email"
              />
            </div>
            <div>
              <Label htmlFor="u-name">Name</Label>
              <Input
                id="u-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5"
                data-testid="admin-users-field-name"
              />
            </div>
            <div>
              <Label htmlFor="u-pw">{editing ? "New password (leave blank to keep current)" : "Password"}</Label>
              <Input
                id="u-pw"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5"
                data-testid="admin-users-field-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                data-testid="admin-users-field-role"
              >
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1.5">{ROLE_DESC[form.role]}</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="admin-users-save-btn">
              {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
